import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation, useApolloClient } from "react-apollo";
import { Title, Field, Control, Button, Fieldset, Tag } from "rbx";
import {
  useCurrentUser,
  useCompanyParameters,
  useSystemCodes
} from "../../utils/hooks";
import { getUTCFormattedDate, ourToast as toast } from "../../utils";
import Loading from "../Loading";

import {
  SINGLE_VEHICLE_OUT_OF_SERVICE_QUERY,
  CREATE_VEHICLE_OUT_OF_SERVICE_MUTATION,
  UPDATE_VEHICLE_OUT_OF_SERVICE_MUTATION,
  SINGLE_VEHICLE_QUERY
} from "../../graphql/Vehicles/Vehicles.graphql";

import { ALL_WORK_ORDERS_QUERY } from "../../graphql/WorkOrders/WorkOrders.graphql";

import VehicleOutOfServiceForm from "./VehicleOutOfServiceForm";

const INITIAL_STATE = {
  RecId: null,
  VehicleRecIdRef: null,
  OutOfServiceDate: new Date().toDateString(),
  StatusRecIdRef: null,
  Comment: null
};

class CustomVehicleOutOfServiceModal extends Error {}

const VehicleOutOfServiceModal = ({
  onComplete,
  id,
  vehicleId,
  excludedStatus,
  WorkOrder,
  StatusByDefault,
  CommentByDefault
}) => {
  const client = useApolloClient();
  const VehicleOutOfService = useQuery(SINGLE_VEHICLE_OUT_OF_SERVICE_QUERY, {
    variables: { where: { RecId: parseInt(id) || null } },
    fetchPolicy: "network-only"
  });
  const Vehicle = useQuery(SINGLE_VEHICLE_QUERY, {
    variables: { where: { RecId: parseInt(vehicleId) || null } },
    fetchPolicy: "network-only"
  });
  const VehicleStatus = useSystemCodes({
    Category: "Fleet",
    CodeName: "VehicleStatus"
  });

  const { data: user } = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState({
    ...INITIAL_STATE
  });
  const { data: params } = useCompanyParameters();

  useEffect(() => {
    if (CommentByDefault && inputs.Comment === "") {
      setInputs(prev => ({
        ...prev,
        Comment: CommentByDefault
      }));
    }
  }, [CommentByDefault, inputs.Comment]);

  useEffect(() => {
    if (StatusByDefault && inputs.StatusRecIdRef === "") {
      setInputs(prev => ({
        ...prev,
        StatusRecIdRef: StatusByDefault
      }));
    }
  }, [StatusByDefault, inputs.StatusRecIdRef]);

  useEffect(() => {
    if (
      VehicleOutOfService?.data?.VehicleOutOfService &&
      !VehicleOutOfService.loading &&
      !inputs.RecId
    ) {
      const {
        VehicleRecIdRef,
        OutOfServiceDate,
        StatusRecIdRef,
        Comment,
        Status,
        RecId
      } = VehicleOutOfService.data.VehicleOutOfService;
      setInputs(prev => ({
        ...prev,
        RecId,
        VehicleRecIdRef,
        OutOfServiceDate,
        StatusRecIdRef,
        Comment,
        Status
      }));
    }
  }, [VehicleOutOfService, inputs.RecId]);

  useEffect(() => {
    if (
      vehicleId &&
      Vehicle &&
      Vehicle.data &&
      !inputs.VehicleRecIdRef &&
      !Vehicle.loading &&
      Vehicle.data.Vehicle &&
      !inputs.RecId
    ) {
      setInputs(prev => ({
        ...prev,
        VehicleRecIdRef: vehicleId,
        RecId: "isNew"
      }));
    }
  }, [Vehicle, inputs.RecId, inputs.VehicleRecIdRef, vehicleId]);

  useEffect(() => {
    if (
      WorkOrder &&
      WorkOrder.WorkOrder &&
      !inputs.Comment &&
      VehicleStatus &&
      Array.isArray(VehicleStatus.data) &&
      VehicleStatus.data.length > 0 &&
      vehicleId &&
      !inputs.VehicleRecIdRef &&
      !inputs.StatusRecIdRef &&
      Vehicle &&
      Vehicle.data &&
      !Vehicle.loading &&
      Vehicle.data.Vehicle
    ) {
      let StatusRecIdRef = null;
      if (WorkOrder.Vendor) {
        StatusRecIdRef = VehicleStatus.data.find(s => s.CodeId === "VS").RecId;
      }
      setInputs({
        ...inputs,
        Comment: `WO # ${WorkOrder.WorkOrder} ${
          WorkOrder.Vendor ? `- ${WorkOrder.Vendor.Name}` : ""
        }`,
        StatusRecIdRef,
        OutOfServiceDate: new Date().toDateString(),
        VehicleRecIdRef: Vehicle.data.Vehicle.RecId,
        RecId: "isNew"
      });
    }
  }, [
    StatusByDefault,
    Vehicle,
    VehicleOutOfService,
    VehicleStatus,
    WorkOrder,
    inputs,
    inputs.RecId,
    params,
    vehicleId
  ]);

  const onChange = async e => {
    const updates = { ...inputs };
    if (e.value && !e.target) {
      updates[e.name] = {
        ...e,
        label: e.label.replace(`- ${e.otherLabel}`, "")
      };
    } else {
      if (
        e.target.name === "StatusRecIdRef" &&
        VehicleStatus &&
        Array.isArray(VehicleStatus.data) &&
        VehicleStatus.data.length > 0 &&
        WorkOrder &&
        WorkOrder.WorkOrder
      ) {
        const availableStatus = VehicleStatus.data.find(s => s.CodeId === "A");
        if (parseInt(e.target.value) === parseInt(availableStatus.RecId)) {
          updates.Comment = `WO # ${WorkOrder.WorkOrder} Repair Complete`;
        }
      }
      updates[e.target.name] =
        e.target.type === "checkbox" ? e.target.checked : e.target.value;
    }
    setInputs(updates);
  };

  const [
    updateVehicleOutOfService,
    updateVehicleOutOfServiceData
  ] = useMutation(UPDATE_VEHICLE_OUT_OF_SERVICE_MUTATION, {
    refetchQueries: [
      {
        query: SINGLE_VEHICLE_QUERY,
        variables: {
          where: {
            RecId: parseInt(vehicleId, 10)
          }
        }
      },
      "ALL_VEHICLE_OUT_OF_SERVICES_VIEW_BY_PAGE_QUERY",
      "SINGLE_VEHICLE_OUT_OF_SERVICE_QUERY",
      "ALL_VEHICLES_VIEW_BY_PAGE_QUERY",
      "ALL_VEHICLES_QUERY",
      "SINGLE_WORK_ORDER_QUERY"
    ]
  });

  const [
    createVehicleOutOfService,
    createVehicleOutOfServiceData
  ] = useMutation(CREATE_VEHICLE_OUT_OF_SERVICE_MUTATION, {
    refetchQueries: [
      {
        query: SINGLE_VEHICLE_QUERY,
        variables: {
          where: {
            RecId: parseInt(vehicleId, 10)
          }
        }
      },
      "ALL_VEHICLE_OUT_OF_SERVICES_VIEW_BY_PAGE_QUERY",
      "SINGLE_VEHICLE_OUT_OF_SERVICE_QUERY",
      "ALL_VEHICLES_VIEW_BY_PAGE_QUERY",
      "ALL_VEHICLES_QUERY",
      "SINGLE_WORK_ORDER_QUERY"
    ]
  });

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (user) {
        setLoading(true);
        const data = {
          VehicleRecIdRef:
            inputs.VehicleRecIdRef && parseInt(inputs.VehicleRecIdRef),
          OutOfServiceDate: getUTCFormattedDate(inputs.OutOfServiceDate),
          StatusRecIdRef:
            inputs.StatusRecIdRef && parseInt(inputs.StatusRecIdRef),
          Comment: inputs.Comment
        };
        const currentVehicleStatusRecId =
          Vehicle?.data?.Vehicle?.StatusRecIdRef;

        const availableStatusRecId = VehicleStatus?.data?.find(
          s => s.CodeId === "A"
        )?.RecId;
        const inShopStatusRecId = VehicleStatus?.data?.find(
          s => s.CodeId === "O"
        )?.RecId;

        if (
          currentVehicleStatusRecId === inShopStatusRecId &&
          parseInt(inputs.StatusRecIdRef, 10) === availableStatusRecId
        ) {
          const { data: WorkOrderData } = await client.query({
            query: ALL_WORK_ORDERS_QUERY,
            variables: {
              where: {
                VehicleRecIdRef: parseInt(inputs.VehicleRecIdRef, 10),
                CompletedDate: null
              },
              first: 1
            },
            fetchPolicy: "network-only"
          });

          const workOrders = WorkOrderData?.WorkOrders || [];
          if (workOrders.length) {
            const workOrderNumber = workOrders[0].WorkOrder;
            throw new CustomVehicleOutOfServiceModal(
              `This vehicle is currently being serviced on incomplete work order #${workOrderNumber}. Please complete work order to update vehicle status.`
            );
          }
        }

        if (!inputs.RecId || vehicleId) {
          await createVehicleOutOfService({
            variables: {
              data
            }
          });
          toast.success("Vehicle out of service created successfully.");
        } else {
          await updateVehicleOutOfService({
            variables: {
              data,
              where: { RecId: parseInt(inputs.RecId) }
            }
          });
          toast.success("Vehicle out of service updated successfully.");
        }
        setInputs({ ...INITIAL_STATE });
        onComplete(inputs.StatusRecIdRef);
        setLoading(false);
      }
    } catch (err) {
      const message =
        err instanceof CustomVehicleOutOfServiceModal
          ? err.message
          : `Error ${
              !inputs.RecId || vehicleId ? "updating" : "creating"
            } Vehicle Out Of Service.`;

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  if (
    loading ||
    (updateVehicleOutOfServiceData && updateVehicleOutOfServiceData.loading) ||
    (VehicleOutOfService && VehicleOutOfService.loading) ||
    (updateVehicleOutOfServiceData && updateVehicleOutOfServiceData.loading) ||
    (createVehicleOutOfServiceData && createVehicleOutOfServiceData.loading)
  )
    return <Loading />;
  const canSaveOrEdit =
    !inputs.VehicleRecIdRef ||
    !inputs.StatusRecIdRef ||
    !inputs.OutOfServiceDate;

  const vehicleStatus = Vehicle?.data?.Vehicle?.Status?.CodeDescription;

  return (
    <form className="add-item-form" id="add-item-form" onSubmit={handleSave}>
      <div className="page-head">
        <div className="page-head-start">
          <Title size={4}>Vehicle Out Of Service</Title>
        </div>
        <div className="page-head-end">
          {vehicleStatus && <Tag color="success">{vehicleStatus}</Tag>}
        </div>
      </div>
      <Fieldset disabled={loading}>
        <VehicleOutOfServiceForm
          excludedStatuses={[
            ...new Set(
              [excludedStatus, Vehicle?.data?.Vehicle?.StatusRecIdRef].filter(
                Boolean
              )
            )
          ]}
          inputs={inputs}
          onChange={onChange}
        />
        <hr />
        <Field kind="group">
          <Control>
            <Button
              color="primary"
              disabled={canSaveOrEdit}
              form="add-item-form"
              type="submit"
            >
              Submit
            </Button>
          </Control>
          <Control>
            <Button type="button" onClick={onComplete}>
              Cancel
            </Button>
          </Control>
        </Field>
      </Fieldset>
    </form>
  );
};

VehicleOutOfServiceModal.propTypes = {
  onComplete: PropTypes.func,
  id: PropTypes.string,
  vehicleId: PropTypes.string,
  excludedStatus: PropTypes.number,
  WorkOrder: PropTypes.object,
  StatusByDefault: PropTypes.string,
  CommentByDefault: PropTypes.string
};

VehicleOutOfServiceModal.defaultProps = {
  vehicleId: null,
  excludedStatus: null,
  WorkOrder: null,
  StatusByDefault: null,
  CommentByDefault: null
};

export default VehicleOutOfServiceModal;
